import React, { useContext } from 'react'

import * as R from 'ramda'

import { Div, Input } from '@driftt/tide-core'
import { Select } from '@driftt/tide-selects'

import { OrgUsersContext } from 'context/orgUsersContext'
import { getDisplayName } from 'utils/user'

import styles from './styles.module.css'

interface Props {
  onFilterChange: (filterParam: string, filterValue: any) => void
  appliedFilters: Record<string, any>
  dateRange: any
  setDateRange: (dateRange: any) => void
  searchQuery: string | undefined
  setSearchQuery: (query: string) => void
}

const DEAL_TYPES = [
  'Churn',
  'Cross-Sell',
  'Expansion',
  'New Business',
  'No Change',
  'Reactivation',
  'Term Change',
  'Upgrade'
]

const CLOSE_DATE_VALUES = [
  {
    label: 'This Quarter',
    from: 1643698800000,
    to: 1651384800000
  },
  {
    label: 'Next Quarter',
    from: 1651384800000,
    to: 1651384800000
  },
  {
    label: 'This Month',
    from: 1643698800000,
    to: 1646118000000
  },
  {
    label: 'Next Month',
    from: 1646118000000,
    to: 1648792800000
  },
  {
    label: 'This Year',
    from: 1643698800000,
    to: 1675234800000
  }
]

const DealsFilter: React.FC<Props> = ({ 
  onFilterChange, 
  appliedFilters, 
  dateRange,
  setDateRange,
  searchQuery, 
  setSearchQuery 
}) => {
  const orgUsersById = useContext(OrgUsersContext)
  const dealOwners = R.values(orgUsersById).filter(
      user => user && user.status === 'ENABLED' && (user.email || user.name)
    )
    .sort((u1, u2) => getDisplayName(u1).localeCompare(getDisplayName(u2)))
  const selectedOwners = appliedFilters['accountOwnerIds']?.map((userId: number) => orgUsersById[userId])

  function handleOwnerChange(owners: any[]) {
    const ownerIdsToFilter = owners.map(owner => owner.id)
    onFilterChange('accountOwnerIds', ownerIdsToFilter.length > 0 ? ownerIdsToFilter : undefined)
  }

  function handleCloseDateChange(closeDates: any) {
    setDateRange(closeDates)
  }

  function handleTypeChange(types: any[]) {
    onFilterChange('dealTypes', types.length > 0 ? types : undefined)
  }

  function handleSearchChange(e: any) {
    setSearchQuery(e.target.value)
  }

  return (
    <Div className={styles.DealsFilter}>
      <Select
        className={styles.DealsOwnerFilter}
        placeholder="All owners"
        onChange={handleOwnerChange}
        getOptionLabel={getDisplayName}
        getOptionValue={R.prop('id')}
        options={dealOwners}
        value={selectedOwners}
        isMulti
      />
      <Select
        className={styles.DealsDateFilter}
        placeholder="Any close date"
        onChange={handleCloseDateChange}
        getOptionLabel={R.prop('label')}
        getOptionValue={(range: any) => range}
        options={CLOSE_DATE_VALUES}
        value={dateRange}
      />
      <Select
        className={styles.DealsTypeFilter}
        placeholder="All types"
        onChange={handleTypeChange}
        getOptionLabel={(option: string) => option}
        getOptionValue={(option: string) => option}
        options={DEAL_TYPES}
        value={appliedFilters['dealTypes']}
        isMulti
      />
      <Input
        className={styles.SearchInput}
        type="search"
        placeholder="Search by deal name..."
        size="small"
        defaultValue={searchQuery}
        onChange={handleSearchChange}
      />
    </Div>
  )
}

export default DealsFilter
